import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Validators, FormGroup, FormControl } from '@angular/forms';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule, SlicePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ServiceService } from '../../../services/service.service';
import { AuthService } from '../../../services/auth.service';
import { StorageService } from '../../../services/storage.service';
import { SiteConfigurationFormData } from '../../../models/site-configuration.interface';
import { Router } from '@angular/router';
import type { Schema } from '../../../../../amplify/data/resource';
import { SiteConfigurationMetadata } from '../../../models/site-configuration-metadata.interface';
import { DeleteSiteConfigurationDialogComponent } from './delete-site-configuration-dialog/delete-site-configuration-dialog.component';

type SiteConfigurationSchema = Schema['SiteConfiguration']['type'];
type HotelPartnerSchema = Schema['HotelPartner']['type'];

@Component({
    selector: 'app-admin-page',
    templateUrl: './admin-page.component.html',
    styleUrls: ['./admin-page.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        CommonModule,
        RouterModule,
        MatPaginatorModule,
        MatSortModule,
        MatTableModule,
        MatInputModule,
        MatFormFieldModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatTabsModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatSelectModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDialogModule,
        SlicePipe
    ]
})
export class AdminPageComponent implements OnInit, AfterViewInit {

  // Form setup
  companySetupForm = new FormGroup({
    companyName: new FormControl('', [Validators.required, Validators.minLength(2)]),
    companyDescription: new FormControl('', [Validators.required, Validators.minLength(10)]),
    phone: new FormControl('', [Validators.required]),
    companyEmail: new FormControl('', [Validators.required, Validators.email]),
    companyContactName: new FormControl('', [Validators.required, Validators.minLength(2)]),
    companyAddress: new FormControl('', [Validators.required]),
    companyWebsite: new FormControl('', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]),
    // User account fields
    userEmail: new FormControl('', [Validators.required, Validators.email]),
    userPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
    userFirstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
    userLastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
    userPhoneNumber: new FormControl('', [Validators.required]),
  });

  // Activity creation form
  activityCreationForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(2)]),
    mainActivityType: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required, Validators.minLength(10)]),
    fullDescription: new FormControl('', [Validators.required, Validators.minLength(20)]),
    duration: new FormControl('', [Validators.required]),
    pricePerPerson: new FormControl(0, [Validators.required, Validators.min(0)]),
    discountedPrice: new FormControl(0, [Validators.min(0), Validators.max(100)]),
    currencyType: new FormControl('USD', [Validators.required]),
    topPick: new FormControl(false),
    sellOut: new FormControl(false),
    language: new FormControl('English', [Validators.required]),
    companyId: new FormControl('', [Validators.required]),
    storeId: new FormControl('', [Validators.required])
  });

  // Site configuration form
  siteConfigForm = new FormGroup({
    location: new FormControl('', [Validators.required, Validators.minLength(2)]),
    siteName: new FormControl('', [Validators.required, Validators.minLength(2)]),
    description: new FormControl(''),
    heroHeadline: new FormControl(''),
    primaryColor: new FormControl(''),
    secondaryColor: new FormControl(''),
    active: new FormControl(false)
  });

  hotelPartnerForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(2)]),
    code: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(24)]),
    contactName: new FormControl(''),
    contactEmail: new FormControl('', [Validators.email]),
    contactPhone: new FormControl(''),
    siteLocation: new FormControl('', [Validators.required]),
    address: new FormControl(''),
    commissionRate: new FormControl(0, [Validators.min(0), Validators.max(100)]),
    active: new FormControl(true),
    notes: new FormControl(''),
  });

  // Table setup
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = ['id', 'companyName', 'companyContactName', 'companyContactEmail', 'companyDescription', 'actions'];
  activityDisplayedColumns: string[] = ['id', 'name', 'mainActivityType', 'description', 'pricePerPerson', 'topPick', 'actions'];
  userDisplayedColumns: string[] = ['id', 'email', 'firstName', 'lastName', 'phoneNumber', 'createdAt', 'actions'];
  siteConfigDisplayedColumns: string[] = ['location', 'siteName', 'active', 'logo', 'actions'];
  hotelPartnerDisplayedColumns: string[] = ['code', 'name', 'siteLocation', 'contact', 'commissionRate', 'active', 'actions'];
  dataSource: MatTableDataSource<any>;
  activityDataSource: MatTableDataSource<any>;
  userDataSource: MatTableDataSource<any>;
  siteConfigDataSource: MatTableDataSource<any>;
  hotelPartnerDataSource: MatTableDataSource<HotelPartnerSchema>;

  // Data properties
  companies: any[] = [];
  activities: any[] = [];
  bookings: any[] = [];
  users: any[] = [];
  stores: any[] = [];
  filteredStores: any[] = [];
  siteConfigurations: SiteConfigurationSchema[] = [];
  hotelPartners: HotelPartnerSchema[] = [];
  selectedHotelPartner: HotelPartnerSchema | null = null;
  selectedSiteConfig: SiteConfigurationSchema | null = null;
  showSiteConfigForm: boolean = false;
  isCreatingSiteConfig: boolean = false;
  
  // File uploads for site configuration
  logoFile: File | null = null;
  headerImageFile: File | null = null;
  faviconFile: File | null = null;
  logoPreview: string | null = null;
  headerImagePreview: string | null = null;
  faviconPreview: string | null = null;

  // Main page hero media uploads
  heroImageFile: File | null = null;
  heroVideoFile: File | null = null;
  heroImagePreview: string | null = null;
  heroVideoPreview: string | null = null;

  /** Preset names for the main page category tiles (dropdown + custom). */
  readonly categoryNamePresets: string[] = [
    'Snorkeling',
    'Suba Diving',
    'Boat Tours',
    'Bus Tours',
    'Beach Tours',
    "4x4 & ATV's",
    'Wild Life',
    'Luxery & Private Tours',
    'Hotel',
    'Car Rental'
  ];
  readonly CATEGORY_SLOT_COUNT = 10;
  /** Mat-select value for "Custom" option; not a real category name. */
  readonly CUSTOM_CATEGORY_VALUE = '__CUSTOM__';
  /** Index 0..9 for template loop */
  readonly categorySlotIndexes = Array.from({ length: 10 }, (_, i) => i);

  /** Per slot: selected preset name, or `CUSTOM_CATEGORY_VALUE` for custom label. */
  categorySlotSelectValue: string[] = [];
  /** When custom is selected, the typed category name. */
  categorySlotCustomName: string[] = [];
  categoryImageFilesBySlot: (File | null)[] = [];
  categoryImagePreviewsBySlot: (string | null)[] = [];

  // Activity creation options
  mainActivityOptions: string[] = ['Snorkeling', 'Diving', 'Boat Tours', 'Bus Tours', 'Beach Tours', 'ATV Tours', 'Hiking', 'Cultural Tours'];
  durationOptions: string[] = ['1-3 hours', '3-5 hours', '5-7 hours', 'Full day (7+ hours)', 'Multi-day'];
  languageOptions: string[] = ['English', 'Spanish', 'Dutch', 'Papiamento'];
  currencyOptions: string[] = ['USD', 'EUR', 'ANG'];

  // Stats
  totalCompanies: number = 0;
  totalActivities: number = 0;
  totalBookings: number = 0;
  totalUsers: number = 0;
  totalHotelPartners: number = 0;

  // UI state
  isLoading: boolean = false;
  selectedCompany: any = null;
  selectedActivity: any = null;
  selectedUser: any = null;
  currentTabIndex: number = 0;
  showActivityForm: boolean = false;
  isCreatingActivity: boolean = false;

  // Messages
  messageDuration: number = 3000;

  constructor(
    private service: ServiceService,
    private authService: AuthService,
    private storageService: StorageService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.dataSource = new MatTableDataSource(this.companies);
    this.activityDataSource = new MatTableDataSource(this.activities);
    this.userDataSource = new MatTableDataSource(this.users);
    this.siteConfigDataSource = new MatTableDataSource(this.siteConfigurations);
    this.hotelPartnerDataSource = new MatTableDataSource(this.hotelPartners);

    this.resetCategorySlotState();
  }

  ngOnInit() {
    this.loadDashboardData();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.activityDataSource.paginator = this.paginator;
    this.activityDataSource.sort = this.sort;
    this.userDataSource.paginator = this.paginator;
    this.userDataSource.sort = this.sort;
    // Note: siteConfigDataSource doesn't need paginator/sort for now
    // If needed, add @ViewChild references and set them here
  }

  /**
   * Load all dashboard data
   */
  loadDashboardData() {
    this.isLoading = true;
    
    // Load companies
    this.service.getCompanies().subscribe({
      next: (companies) => {
        this.companies = companies;
        this.totalCompanies = companies.length;
        this.dataSource.data = companies;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.showMessage('Error loading companies', 'error');
        this.isLoading = false;
      }
    });

    // Load activities
    this.service.getActivities().subscribe({
      next: (activities) => {
        this.activities = activities;
        this.totalActivities = activities.length;
      },
      error: (error) => {
        console.error('Error loading activities:', error);
      }
    });

    // Load bookings
    this.service.getBookings().subscribe({
      next: (bookings) => {
        this.bookings = bookings;
        this.totalBookings = bookings.length;
      },
      error: (error) => {
        console.error('Error loading bookings:', error);
      }
    });

    // Load users
    this.service.getUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.totalUsers = users.length;
        this.userDataSource.data = users;
      },
      error: (error) => {
        console.error('Error loading users:', error);
      }
    });

    // Load stores
    this.service.getStores().subscribe({
      next: (stores) => {
        this.stores = stores;
      },
      error: (error) => {
        console.error('Error loading stores:', error);
      }
    });

    this.loadHotelPartners();
  }

  /**
   * Handle form submission
   */
  async onSubmit() {
    if (this.companySetupForm.valid) {
      this.isLoading = true;
      const formData = this.companySetupForm.value;

      try {
        // First create the company
        // Map form fields to Company schema fields
        const companyData: any = {
          name: formData.companyName || '',
          description: formData.companyDescription || '',
          contactEmail: formData.companyEmail || '',
          contactName: formData.companyContactName || '',
          contactPhone: formData.phone || '',
          address: formData.companyAddress || '',
          website: formData.companyWebsite || '',
          status: 'pending', // Set initial status
        };
        
        console.log('Company data being sent:', companyData);

        let newCompany;
        try {
          newCompany = await firstValueFrom(this.service.createCompany(companyData));
          console.log('Company created successfully:', newCompany);
          
          // Validate that company was actually created
          if (!newCompany || !newCompany.id) {
            console.error('Company creation returned null or invalid data');
            this.showMessage('Failed to create company: Invalid response from server.', 'error');
            this.isLoading = false;
            return; // Exit gracefully without creating user
          }
        } catch (companyError) {
          console.error('Error creating company:', companyError);
          this.showMessage('Failed to create company. Please check the form and try again.', 'error');
          this.isLoading = false;
          return; // Exit gracefully without creating user
        }
        
        // Only proceed with user creation if company was created successfully
        try {
          const userData: any = {
            email: formData.userEmail || '',
            firstName: formData.userFirstName || '',
            lastName: formData.userLastName || '',
            phoneNumber: formData.userPhoneNumber || '',
          };

          // Create user in Auth (Cognito) with company group
          await this.authService.signUpWithGroup(
            formData.userEmail || '', 
            formData.userPassword || '', 
            'company'
          );
          
          // Create user record in database
          const newUser = await firstValueFrom(this.service.createUser(userData));

          // Assign user to company group
          try {
            await this.authService.assignUserToGroup(
              formData.userEmail || '', 
              'company'
            );
            console.log('User assigned to company group successfully');
          } catch (groupError) {
            console.warn('Failed to assign user to company group:', groupError);
            // Don't fail the entire process if group assignment fails
          }

          // Update local data
          this.companies.push(newCompany);
          this.dataSource.data = [...this.companies];
          this.totalCompanies = this.companies.length;
          this.resetForm();
          
          this.showMessage('Company and user account created successfully! The user has been assigned to the company group and will need to verify their email.', 'success');
        } catch (userError) {
          console.error('Error creating user:', userError);
          // Company was created but user creation failed
          // Clean up: we could delete the company here if desired
          this.showMessage('Company created successfully, but failed to create user account. Please contact support.', 'info');
          
          // Update local data with company
          this.companies.push(newCompany);
          this.dataSource.data = [...this.companies];
          this.totalCompanies = this.companies.length;
          this.resetForm();
        }
        
        this.isLoading = false;
        
      } catch (error) {
        console.error('Unexpected error:', error);
        this.showMessage('An unexpected error occurred. Please try again.', 'error');
        this.isLoading = false;
      }
    } else {
      this.markAllTouched(this.companySetupForm);
    }
  }

  /**
   * Reset the form
   */
  resetForm() {
    this.companySetupForm.reset();
    this.companySetupForm.markAsUntouched();
  }

  private markAllTouched(form: FormGroup): void {
    Object.values(form.controls).forEach((c) => c.markAsTouched());
  }

  /**
   * Apply filter to the table
   */
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Handle tab change
   */
  onTabChange(event: any) {
    this.currentTabIndex = event.index;
    
    switch (this.currentTabIndex) {
      case 0: // Company Management
        this.loadCompanies();
        break;
      case 1: // System Settings (second tab in template)
        this.loadSiteConfigurations();
        break;
      case 2:
        this.loadHotelPartners();
        break;
      default:
        break;
    }
  }

  /**
   * Load companies data
   */
  loadCompanies() {
    this.isLoading = true;
    this.service.getCompanies().subscribe({
      next: (companies) => {
        this.companies = companies;
        this.dataSource.data = companies;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.showMessage('Error loading companies', 'error');
        this.isLoading = false;
      }
    });
  }

  /**
   * Load activities data
   */
  loadActivities() {
    this.isLoading = true;
    this.service.getActivities().subscribe({
      next: (activities) => {
        this.activities = activities;
        this.activityDataSource.data = activities;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading activities:', error);
        this.showMessage('Error loading activities', 'error');
        this.isLoading = false;
      }
    });
  }

  /**
   * Load users data
   */
  loadUsers() {
    this.isLoading = true;
    this.service.getUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.userDataSource.data = users;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.showMessage('Error loading users', 'error');
        this.isLoading = false;
      }
    });
  }

  /**
   * Edit company
   */
  editCompany(company: any) {
    this.selectedCompany = company;
    this.companySetupForm.patchValue({
      companyName: company.name || '',
      companyDescription: company.description || '',
      companyEmail: company.contactEmail || '',
      companyContactName: company.contactName || '',
    });
    document.querySelector('.form-card')?.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * View company details
   */
  viewCompanyDetails(company: any) {
    // You can implement a dialog or navigate to a details page
    this.showMessage(`Viewing details for ${company.name || 'Unknown Company'}`, 'info');
  }

  navigateToCompanyDashboard(company: any) {
    // Navigate to the company's dashboard with their company ID
    this.router.navigate(['/backoffice/all-stores'], { queryParams: { companyId: company.id } });
  }

  /**
   * Delete company with cascade deletion
   */
  deleteCompany(company: any) {
    const companyName = company.name || 'this company';
    const confirmMessage = `Are you sure you want to delete ${companyName}?\n\nThis will permanently delete:\n• Company information\n• All associated user accounts\n• All stores and locations\n• All activities and bookings\n• All related data\n\nThis action cannot be undone.`;
    
    if (confirm(confirmMessage)) {
      this.isLoading = true;
      this.showMessage('Deleting company and all associated data...', 'info');
      
      this.service.deleteCompanyCascade(company.id).subscribe({
        next: (result) => {
          console.log('Cascade deletion result:', result);
          
          // Update local data
          this.companies = this.companies.filter(c => c.id !== company.id);
          this.dataSource.data = [...this.companies];
          this.totalCompanies = this.companies.length;
          
          // Show detailed success message
          let successMessage = `Company "${companyName}" and all associated data deleted successfully.`;
          
          if (result.errors && result.errors.length > 0) {
            successMessage += `\n\nNote: Some items could not be deleted:\n${result.errors.slice(0, 3).join('\n')}`;
            if (result.errors.length > 3) {
              successMessage += `\n... and ${result.errors.length - 3} more errors.`;
            }
          }
          
          this.showMessage(successMessage, result.errors.length > 0 ? 'error' : 'success');
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error deleting company:', error);
          this.showMessage(`Error deleting company: ${error.message}`, 'error');
          this.isLoading = false;
        }
      });
    }
  }

  /**
   * Add new activity
   */
  addActivity() {
    this.showActivityForm = true;
    this.resetActivityForm();
  }

  /**
   * Submit activity creation form
   */
  onSubmitActivity() {
    if (this.activityCreationForm.valid) {
      this.isCreatingActivity = true;
      const formData = this.activityCreationForm.value;

      const activityData: any = {
        name: formData.name || '',
        mainActivityType: formData.mainActivityType || '',
        description: formData.description || '',
        fullDescription: formData.fullDescription || '',
        duration: formData.duration || '',
        pricePerPerson: formData.pricePerPerson || 0,
        discountedPrice: formData.discountedPrice || 0,
        currencyType: formData.currencyType || 'USD',
        topPick: formData.topPick || false,
        sellOut: formData.sellOut || false,
        language: formData.language || 'English',
        storeID: formData.storeId || '',
        // Note: companyId is not directly stored in Activity model, it's inferred through store relationship
      };

      this.service.createActivity(activityData).subscribe({
        next: (newActivity) => {
          this.activities.push(newActivity);
          this.activityDataSource.data = [...this.activities];
          this.totalActivities = this.activities.length;
          this.resetActivityForm();
          this.showActivityForm = false;
          this.showMessage('Activity created successfully!', 'success');
          this.isCreatingActivity = false;
        },
        error: (error) => {
          console.error('Error creating activity:', error);
          this.showMessage('Error creating activity', 'error');
          this.isCreatingActivity = false;
        }
      });
    } else {
      this.markAllTouched(this.activityCreationForm);
    }
  }

  /**
   * Reset the activity form
   */
  resetActivityForm() {
    this.activityCreationForm.reset();
    this.activityCreationForm.patchValue({
      currencyType: 'USD',
      language: 'English',
      topPick: false,
      sellOut: false,
      pricePerPerson: 0,
      discountedPrice: 0
    });
    this.activityCreationForm.markAsUntouched();
  }


  /**
   * Edit activity
   */
  editActivity(activity: any) {
    this.selectedActivity = activity;
    this.activityCreationForm.patchValue({
      name: activity.name || '',
      mainActivityType: activity.mainActivityType || '',
      description: activity.description || '',
      fullDescription: activity.fullDescription || '',
      duration: activity.duration || '',
      pricePerPerson: activity.pricePerPerson || 0,
      discountedPrice: activity.discountedPrice || 0,
      currencyType: activity.currencyType || 'USD',
      topPick: activity.topPick || false,
      sellOut: activity.sellOut || false,
      language: activity.language || 'English',
      companyId: activity.companyId || '',
      storeId: activity.storeID || ''
    });
    
    this.showActivityForm = true;
    // Scroll to form
    document.querySelector('.activity-form-card')?.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * Delete activity
   */
  deleteActivity(activity: any) {
    if (confirm(`Are you sure you want to delete "${activity.name || 'this activity'}"?`)) {
      this.isLoading = true;
      this.service.deleteActivity(activity.id).subscribe({
        next: () => {
          this.activities = this.activities.filter(a => a.id !== activity.id);
          this.activityDataSource.data = [...this.activities];
          this.totalActivities = this.activities.length;
          this.showMessage('Activity deleted successfully', 'success');
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error deleting activity:', error);
          this.showMessage('Error deleting activity', 'error');
          this.isLoading = false;
        }
      });
    }
  }

  /**
   * Cancel activity creation
   */
  cancelActivityCreation() {
    this.showActivityForm = false;
    this.resetActivityForm();
    this.selectedActivity = null;
  }

  /**
   * Handle company selection change
   */
  onCompanySelectionChange(companyId: string) {
    this.activityCreationForm.patchValue({ companyId });
    // Filter stores by company (if there's a relationship)
    // For now, we'll show all stores since the relationship might not be implemented yet
    this.filteredStores = this.stores;
  }

  /**
   * Handle store selection change
   */
  onStoreSelectionChange(storeId: string) {
    this.activityCreationForm.patchValue({ storeId });
  }

  /**
   * View user details
   */
  viewUserDetails(user: any) {
    this.showMessage(`Viewing details for ${user.firstName || 'Unknown'} ${user.lastName || 'User'}`, 'info');
  }

  /**
   * Delete user
   */
  deleteUser(user: any) {
    if (confirm(`Are you sure you want to delete user ${user.firstName || 'Unknown'} ${user.lastName || 'User'}?`)) {
      this.isLoading = true;
      this.service.deleteUser(user.id).subscribe({
        next: () => {
          this.users = this.users.filter(u => u.id !== user.id);
          this.userDataSource.data = [...this.users];
          this.totalUsers = this.users.length;
          this.showMessage('User deleted successfully', 'success');
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error deleting user:', error);
          this.showMessage('Error deleting user', 'error');
          this.isLoading = false;
        }
      });
    }
  }

  /**
   * Show message to user
   */
  showMessage(message: string, type: 'success' | 'error' | 'info' = 'info') {
    this.snackBar.open(message, 'Close', {
      duration: this.messageDuration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [`snackbar-${type}`]
    });
  }

  /**
   * Get form controls for template access
   */
  get f() {
    return this.companySetupForm.controls;
  }

  /**
   * Get activity form controls for template access
   */
  get af() {
    return this.activityCreationForm.controls;
  }

  /**
   * Get site config form controls for template access
   */
  get scf() {
    return this.siteConfigForm.controls;
  }

  get hpf() {
    return this.hotelPartnerForm.controls;
  }

  // ==================== HOTEL PARTNER METHODS ====================

  loadHotelPartners() {
    this.service.getHotelPartners().subscribe({
      next: (hotels) => {
        this.hotelPartners = hotels;
        this.totalHotelPartners = hotels.length;
        this.hotelPartnerDataSource.data = [...hotels];
      },
      error: (error) => {
        console.error('Error loading hotel partners:', error);
        this.showMessage('Error loading hotel partners', 'error');
      }
    });
  }

  async onSubmitHotelPartner() {
    if (this.hotelPartnerForm.invalid) {
      this.markAllTouched(this.hotelPartnerForm);
      return;
    }

    const formData = this.hotelPartnerForm.value;
    const hotelData: any = {
      name: formData.name?.trim() || '',
      code: this.normalizeHotelCode(formData.code),
      contactName: formData.contactName?.trim() || '',
      contactEmail: formData.contactEmail?.trim() || '',
      contactPhone: formData.contactPhone?.trim() || '',
      siteLocation: formData.siteLocation?.trim().toLowerCase() || 'default',
      address: formData.address?.trim() || '',
      commissionRate: Number(formData.commissionRate || 0),
      active: formData.active ?? true,
      notes: formData.notes?.trim() || '',
    };

    const duplicate = this.hotelPartners.find((hotel) =>
      hotel.id !== this.selectedHotelPartner?.id &&
      hotel.code?.trim().toUpperCase() === hotelData.code
    );
    if (duplicate) {
      this.showMessage(`Referral code ${hotelData.code} is already used by ${duplicate.name}`, 'error');
      return;
    }

    this.isLoading = true;
    const save$ = this.selectedHotelPartner?.id
      ? this.service.updateHotelPartner(this.selectedHotelPartner.id, hotelData)
      : this.service.createHotelPartner(hotelData);

    save$.subscribe({
      next: () => {
        this.showMessage(
          this.selectedHotelPartner ? 'Hotel partner updated successfully' : 'Hotel partner created successfully',
          'success'
        );
        this.resetHotelPartnerForm();
        this.loadHotelPartners();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error saving hotel partner:', error);
        this.showMessage('Error saving hotel partner', 'error');
        this.isLoading = false;
      }
    });
  }

  editHotelPartner(hotel: HotelPartnerSchema) {
    this.selectedHotelPartner = hotel;
    this.hotelPartnerForm.patchValue({
      name: hotel.name || '',
      code: hotel.code || '',
      contactName: hotel.contactName || '',
      contactEmail: hotel.contactEmail || '',
      contactPhone: hotel.contactPhone || '',
      siteLocation: hotel.siteLocation || '',
      address: hotel.address || '',
      commissionRate: hotel.commissionRate || 0,
      active: hotel.active ?? true,
      notes: hotel.notes || '',
    });
    document.querySelector('.hotel-partner-form-card')?.scrollIntoView({ behavior: 'smooth' });
  }

  deleteHotelPartner(hotel: HotelPartnerSchema) {
    if (!hotel.id) return;
    if (!confirm(`Delete hotel partner "${hotel.name || hotel.code}"? Existing bookings keep their stored referral code.`)) {
      return;
    }

    this.isLoading = true;
    this.service.deleteHotelPartner(hotel.id).subscribe({
      next: () => {
        this.showMessage('Hotel partner deleted successfully', 'success');
        this.loadHotelPartners();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error deleting hotel partner:', error);
        this.showMessage('Error deleting hotel partner', 'error');
        this.isLoading = false;
      }
    });
  }

  resetHotelPartnerForm() {
    this.selectedHotelPartner = null;
    this.hotelPartnerForm.reset({
      name: '',
      code: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      siteLocation: '',
      address: '',
      commissionRate: 0,
      active: true,
      notes: '',
    });
    this.hotelPartnerForm.markAsUntouched();
  }

  normalizeHotelCode(value: unknown): string {
    return (value || '').toString().trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  }

  // ==================== SITE CONFIGURATION METHODS ====================

  /**
   * Load site configurations
   */
  loadSiteConfigurations() {
    this.isLoading = true;
    this.service.getSiteConfigurations().subscribe({
      next: (configs) => {
        const list = Array.isArray(configs)
          ? configs.filter((c): c is SiteConfigurationSchema => c != null && typeof c === 'object')
          : [];
        this.siteConfigurations = list;
        this.siteConfigDataSource.data = [...list];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading site configurations:', error);
        this.showMessage('Error loading site configurations', 'error');
        this.isLoading = false;
      }
    });
  }

  trackBySiteConfigId(_index: number, row: SiteConfigurationSchema): string {
    return row?.id ?? `${row?.location ?? ''}-${row?.siteName ?? ''}-${_index}`;
  }

  /**
   * Add new site configuration
   */
  addSiteConfiguration() {
    this.selectedSiteConfig = null;
    this.resetSiteConfigForm();
    this.showSiteConfigForm = true;
  }

  /**
   * Submit site configuration form
   */
  async onSubmitSiteConfig() {
    if (this.siteConfigForm.valid) {
      this.isCreatingSiteConfig = true;
      const formData = this.siteConfigForm.value;

      try {
        const existingMetadata: SiteConfigurationMetadata = this.safeParseSiteConfigurationMetadata(this.selectedSiteConfig?.metadata);
        const metadataToSave: SiteConfigurationMetadata = {
          ...existingMetadata,
          mainHero: {
            ...(existingMetadata.mainHero ?? {})
          },
          categoryImages: {
            ...(existingMetadata.categoryImages ?? {})
          }
        };

        const configData: any = {
          location: formData.location || '',
          siteName: formData.siteName || '',
          description: formData.description || '',
          primaryColor: formData.primaryColor || '',
          secondaryColor: formData.secondaryColor || '',
          active: formData.active || false
        };

        // Upload logo if provided
        if (this.logoFile) {
          const logoResult = await this.storageService.uploadSiteConfigImage(
            this.logoFile,
            'logo',
            configData.location
          );
          if (logoResult.success) {
            configData.logoUrl = logoResult.url;
            configData.logoKey = logoResult.key;
          }
        }

        // Upload header image if provided
        if (this.headerImageFile) {
          const headerResult = await this.storageService.uploadSiteConfigImage(
            this.headerImageFile,
            'header',
            configData.location
          );
          if (headerResult.success) {
            configData.headerImageUrl = headerResult.url;
            configData.headerImageKey = headerResult.key;
          }
        }

        // Upload favicon if provided
        if (this.faviconFile) {
          const faviconResult = await this.storageService.uploadSiteConfigImage(
            this.faviconFile,
            'favicon',
            configData.location
          );
          if (faviconResult.success) {
            configData.faviconUrl = faviconResult.url;
            configData.faviconKey = faviconResult.key;
          }
        }

        // Upload main hero image/video if provided.
        if (this.heroImageFile) {
          const heroImageResult = await this.storageService.uploadSiteConfigHeroImage(
            this.heroImageFile,
            configData.location
          );
          if (heroImageResult.success) {
            metadataToSave.mainHero = metadataToSave.mainHero ?? {};
            metadataToSave.mainHero.imageUrl = heroImageResult.url;
            metadataToSave.mainHero.imageKey = heroImageResult.key;
          }
        }

        if (this.heroVideoFile) {
          const heroVideoResult = await this.storageService.uploadSiteConfigHeroVideo(
            this.heroVideoFile,
            configData.location
          );
          if (heroVideoResult.success) {
            metadataToSave.mainHero = metadataToSave.mainHero ?? {};
            metadataToSave.mainHero.videoUrl = heroVideoResult.url;
            metadataToSave.mainHero.videoKey = heroVideoResult.key;
          }
        }

        metadataToSave.mainHero = metadataToSave.mainHero ?? {};
        const heroHeadlineVal = formData.heroHeadline?.trim();
        if (heroHeadlineVal) {
          metadataToSave.mainHero.headline = heroHeadlineVal;
        } else {
          delete metadataToSave.mainHero.headline;
        }

        const categoryNames = this.getResolvedCategoryNamesForAllSlots();
        if (!categoryNames) {
          this.isCreatingSiteConfig = false;
          return;
        }

        metadataToSave.categoryOrder = categoryNames;

        const prevOrder = existingMetadata.categoryOrder;
        const prevImages = existingMetadata.categoryImages ?? {};

        metadataToSave.categoryImages = { ...prevImages };

        for (let i = 0; i < this.CATEGORY_SLOT_COUNT; i++) {
          const name = categoryNames[i];
          const file = this.categoryImageFilesBySlot[i];
          if (file) {
            const categoryResult = await this.storageService.uploadSiteConfigCategoryImage(
              file,
              configData.location,
              name
            );
            if (categoryResult.success) {
              metadataToSave.categoryImages![name] = {
                imageUrl: categoryResult.url,
                imageKey: categoryResult.key
              };
            }
          } else {
            if (prevImages[name]) {
              metadataToSave.categoryImages![name] = prevImages[name];
            } else if (prevOrder?.[i] && prevOrder[i] !== name && prevImages[prevOrder[i]]) {
              metadataToSave.categoryImages![name] = { ...prevImages[prevOrder[i]] };
            }
          }
        }

        for (const key of Object.keys(metadataToSave.categoryImages!)) {
          if (!categoryNames.includes(key)) {
            delete metadataToSave.categoryImages![key];
          }
        }

        // Persist the hero + category media in site configuration metadata.
        configData.metadata = JSON.stringify(metadataToSave);

        // If activating this config, deactivate others first
        if (configData.active && this.selectedSiteConfig?.id) {
          await firstValueFrom(this.service.setActiveSiteConfiguration(this.selectedSiteConfig.id));
        } else if (configData.active) {
          // For new configs, we'll activate after creation
          configData.active = false;
        }

        let savedConfig: SiteConfigurationSchema;
        if (this.selectedSiteConfig?.id) {
          // Update existing
          savedConfig = await firstValueFrom(this.service.updateSiteConfiguration(this.selectedSiteConfig.id, configData)) as SiteConfigurationSchema;
          this.showMessage('Site configuration updated successfully!', 'success');
        } else {
          // Create new
          savedConfig = await firstValueFrom(this.service.createSiteConfiguration(configData)) as SiteConfigurationSchema;
          
          // Activate if needed
          if (formData.active && savedConfig.id) {
            await firstValueFrom(this.service.setActiveSiteConfiguration(savedConfig.id));
          }
          
          this.showMessage('Site configuration created successfully!', 'success');
        }

        // Reload configurations
        this.loadSiteConfigurations();
        this.showSiteConfigForm = false;
        this.resetSiteConfigForm();
        this.isCreatingSiteConfig = false;
      } catch (error) {
        console.error('Error saving site configuration:', error);
        this.showMessage('Error saving site configuration', 'error');
        this.isCreatingSiteConfig = false;
      }
    } else {
      this.markAllTouched(this.siteConfigForm);
    }
  }

  /**
   * Edit site configuration
   */
  editSiteConfiguration(config: SiteConfigurationSchema) {
    this.selectedSiteConfig = config;
    const parsedForEdit = this.safeParseSiteConfigurationMetadata(config.metadata);
    this.siteConfigForm.patchValue({
      location: config.location,
      siteName: config.siteName,
      description: config.description || '',
      heroHeadline: parsedForEdit.mainHero?.headline || '',
      primaryColor: config.primaryColor || '',
      secondaryColor: config.secondaryColor || '',
      active: config.active
    });

    // Set previews if images exist
    if (config.logoUrl) {
      this.logoPreview = config.logoUrl;
    }
    if (config.headerImageUrl) {
      this.headerImagePreview = config.headerImageUrl;
    }
    if (config.faviconUrl) {
      this.faviconPreview = config.faviconUrl;
    }

    // Load hero + category previews from metadata (file inputs can't be prefilled).
    this.heroImagePreview = parsedForEdit.mainHero?.imageUrl ?? null;
    this.heroVideoPreview = parsedForEdit.mainHero?.videoUrl ?? null;
    this.loadCategorySlotsFromMetadata(parsedForEdit);

    this.showSiteConfigForm = true;
    document.querySelector('.site-config-form-card')?.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * Delete site configuration (after explicit dialog confirmation).
   * Removes the database record first, then deletes associated storage objects.
   */
  deleteSiteConfiguration(config: SiteConfigurationSchema) {
    if (!config.id) {
      return;
    }

    const storageKeys = this.storageService.collectSiteConfigStorageKeys(config);

    this.dialog
      .open(DeleteSiteConfigurationDialogComponent, {
        width: '560px',
        maxWidth: '96vw',
        disableClose: true,
        autoFocus: 'dialog',
        data: {
          location: config.location ?? '',
          siteName: config.siteName ?? '',
          isActive: !!config.active
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.isLoading = true;
        this.service.deleteSiteConfiguration(config.id!).subscribe({
          next: async () => {
            const failedKeys = await this.storageService.deleteStorageKeys(storageKeys);

            this.siteConfigurations = this.siteConfigurations.filter((c) => c.id !== config.id);
            this.siteConfigDataSource.data = [...this.siteConfigurations];

            if (this.selectedSiteConfig?.id === config.id) {
              this.showSiteConfigForm = false;
              this.resetSiteConfigForm();
            }

            if (failedKeys.length > 0) {
              console.warn('Site config storage cleanup incomplete', failedKeys);
              this.showMessage(
                'Configuration was removed from the database. Some files could not be deleted from storage; check the console or clean up S3 manually.',
                'error'
              );
            } else {
              this.showMessage('Site configuration and related storage files were deleted.', 'success');
            }
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error deleting site configuration:', error);
            this.showMessage('Could not delete the site configuration from the database. No storage files were removed.', 'error');
            this.isLoading = false;
          }
        });
      });
  }

  /**
   * Set active site configuration
   */
  setActiveSiteConfiguration(config: SiteConfigurationSchema) {
    if (!config.id) return;
    
    this.isLoading = true;
    this.service.setActiveSiteConfiguration(config.id).subscribe({
      next: () => {
        this.loadSiteConfigurations();
        this.showMessage(`Site configuration for "${config.location}" is now active`, 'success');
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error setting active site configuration:', error);
        this.showMessage('Error setting active site configuration', 'error');
        this.isLoading = false;
      }
    });
  }

  /**
   * Reset site configuration form
   */
  resetSiteConfigForm() {
    this.siteConfigForm.reset();
    this.siteConfigForm.patchValue({
      active: false
    });
    this.siteConfigForm.markAsUntouched();
    this.logoFile = null;
    this.headerImageFile = null;
    this.faviconFile = null;
    this.logoPreview = null;
    this.headerImagePreview = null;
    this.faviconPreview = null;

    this.heroImageFile = null;
    this.heroVideoFile = null;
    this.heroImagePreview = null;
    this.heroVideoPreview = null;

    this.resetCategorySlotState();

    this.selectedSiteConfig = null;
  }


  /**
   * Handle file selection for logo
   */
  onLogoFileSelected(event: Event) {
    this.handleImageFile(event, (file) => {
      this.logoFile = file;
      this.previewAsDataUrl(file, (url) => (this.logoPreview = url));
    });
  }

  /**
   * Handle file selection for header image
   */
  onHeaderImageFileSelected(event: Event) {
    this.handleImageFile(event, (file) => {
      this.headerImageFile = file;
      this.previewAsDataUrl(file, (url) => (this.headerImagePreview = url));
    });
  }

  /**
   * Handle file selection for favicon
   */
  onFaviconFileSelected(event: Event) {
    this.handleImageFile(event, (file) => {
      this.faviconFile = file;
      this.previewAsDataUrl(file, (url) => (this.faviconPreview = url));
    });
  }

  /**
   * Parse `SiteConfiguration.metadata` safely.
   */
  private safeParseSiteConfigurationMetadata(
    metadata: string | undefined | null | Record<string, unknown>
  ): SiteConfigurationMetadata {
    if (metadata == null || metadata === '') return {};
    if (typeof metadata === 'object' && !Array.isArray(metadata)) {
      return metadata as SiteConfigurationMetadata;
    }
    try {
      const parsed = JSON.parse(metadata as string) as SiteConfigurationMetadata;
      return parsed ?? {};
    } catch {
      return {};
    }
  }

  /**
   * Handle file selection for main hero image.
   */
  onHeroImageFileSelected(event: Event) {
    this.handleImageFile(event, (file) => {
      this.heroImageFile = file;
      this.previewAsDataUrl(file, (url) => (this.heroImagePreview = url));
    });
  }

  /**
   * Handle file selection for main hero video.
   */
  onHeroVideoFileSelected(event: Event) {
    this.handleImageFile(
      event,
      (file) => {
        this.heroVideoFile = file;
        this.heroVideoPreview = URL.createObjectURL(file);
      },
      50,
      ['video/*']
    );
  }

  /**
   * Handle file selection for a category tile image (by slot index 0..9).
   */
  onCategoryImageFileSelected(event: Event, slotIndex: number) {
    this.handleImageFile(event, (file) => {
      this.categoryImageFilesBySlot[slotIndex] = file;
      this.previewAsDataUrl(file, (url) => (this.categoryImagePreviewsBySlot[slotIndex] = url));
    });
  }

  onCategorySlotSelectChange(slotIndex: number, value: string) {
    this.categorySlotSelectValue[slotIndex] = value;
    if (value !== this.CUSTOM_CATEGORY_VALUE) {
      this.categorySlotCustomName[slotIndex] = '';
    }
  }

  onCategorySlotCustomNameInput(slotIndex: number, value: string) {
    this.categorySlotCustomName[slotIndex] = value;
  }

  getResolvedCategoryNameForSlot(slotIndex: number): string {
    const sel = this.categorySlotSelectValue[slotIndex];
    if (sel === this.CUSTOM_CATEGORY_VALUE) {
      return (this.categorySlotCustomName[slotIndex] || '').trim();
    }
    return (sel || '').trim();
  }

  /**
   * Returns resolved names for all slots, or null if validation fails.
   */
  private getResolvedCategoryNamesForAllSlots(): string[] | null {
    const names: string[] = [];
    for (let i = 0; i < this.CATEGORY_SLOT_COUNT; i++) {
      const n = this.getResolvedCategoryNameForSlot(i);
      if (!n) {
        this.showMessage(`Category ${i + 1}: enter a name or choose a preset.`, 'error');
        return null;
      }
      names.push(n);
    }
    const unique = new Set(names);
    if (unique.size !== names.length) {
      this.showMessage('Each category name must be unique.', 'error');
      return null;
    }
    return names;
  }

  private resetCategorySlotState() {
    this.categorySlotSelectValue = [...this.categoryNamePresets];
    this.categorySlotCustomName = Array(this.CATEGORY_SLOT_COUNT).fill('');
    this.categoryImageFilesBySlot = Array(this.CATEGORY_SLOT_COUNT).fill(null);
    this.categoryImagePreviewsBySlot = Array(this.CATEGORY_SLOT_COUNT).fill(null);
  }

  private loadCategorySlotsFromMetadata(parsed: SiteConfigurationMetadata) {
    if (this.categorySlotSelectValue.length !== this.CATEGORY_SLOT_COUNT) {
      this.resetCategorySlotState();
    }

    const order =
      parsed.categoryOrder?.length === this.CATEGORY_SLOT_COUNT
        ? parsed.categoryOrder
        : [...this.categoryNamePresets];

    for (let i = 0; i < this.CATEGORY_SLOT_COUNT; i++) {
      const name = order[i];
      if (this.categoryNamePresets.includes(name)) {
        this.categorySlotSelectValue[i] = name;
        this.categorySlotCustomName[i] = '';
      } else {
        this.categorySlotSelectValue[i] = this.CUSTOM_CATEGORY_VALUE;
        this.categorySlotCustomName[i] = name;
      }

      this.categoryImageFilesBySlot[i] = null;
      const img = parsed.categoryImages?.[name];
      this.categoryImagePreviewsBySlot[i] = img?.imageUrl ?? null;
    }
  }

  /**
   * Cancel site configuration form
   */
  cancelSiteConfigForm() {
    this.showSiteConfigForm = false;
    this.resetSiteConfigForm();
  }

  private handleImageFile(
      event: Event,
      onFile: (file: File) => void,
      maxSizeMB = 5,
      allowedTypes?: string[]
  ): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const validation = allowedTypes
      ? this.storageService.validateFile(file, maxSizeMB, allowedTypes)
      : this.storageService.validateFile(file, maxSizeMB);
    if (!validation.valid) {
      this.showMessage(validation.error ?? 'Invalid file', 'error');
      return;
    }
    onFile(file);
  }

  private previewAsDataUrl(file: File, onResult: (url: string) => void): void {
    const reader = new FileReader();
    reader.onload = (e) => onResult(e.target!.result as string);
    reader.readAsDataURL(file);
  }
}
